// Drucken aus der iPhone-App über das Teilen-Menü (29.10.2026).
//
// Gemessen am Gerät des Betreibers (Drucktest 29.10.2026, nach dem
// Umbau bestätigt): In der App vom Home-Bildschirm öffnet weder
// window.print() noch print() in einem Rahmen einen Dialog. Das Teilen-Menü mit einer Datei dagegen bietet
// „Drucken" an, und der Ausdruck kommt ohne Fußzeile.
//
// Also: das Blatt, das am Bildschirm steht, als Bild aufnehmen
// (html2canvas, MIT, erst bei Bedarf geladen), in A4-Seiten schneiden,
// als PDF verpacken (lib/pdfAusBildern.ts) und teilen.

import { pdfAusBildern, type PdfSeite } from './pdfAusBildern';

/** Auflösung: 3 Bildpunkte je CSS-Pixel ≈ 290 dpi auf A4. */
const MASSSTAB = 3;
/** A4-Breite in CSS-Pixeln (96 dpi). */
const A4_PX = (210 / 25.4) * 96;
const A4_HOEHE_PX = (297 / 25.4) * 96;

/**
 * Nimmt `element` als A4-Blatt auf. `vorbereiten` darf am geklonten
 * Dokument die Druckgestalt herstellen (Breite 210 mm, Ränder) — das
 * Original am Bildschirm bleibt unberührt.
 */
export async function blattAlsPdf(
  element: HTMLElement,
  dateiname: string,
  vorbereiten?: (klon: Document) => void
): Promise<File> {
  const { default: html2canvas } = await import('html2canvas');
  const leinwand = await html2canvas(element, {
    scale: MASSSTAB,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: Math.ceil(A4_PX),
    logging: false,
    onclone: (klon) => vorbereiten?.(klon)
  });

  // In A4-Höhe schneiden. Die Breite ist die Blattbreite; ist die
  // Aufnahme schmaler oder breiter, folgt die Seitenhöhe demselben Maß.
  const seitenHoehe = Math.round((leinwand.width * A4_HOEHE_PX) / A4_PX);
  const seiten: PdfSeite[] = [];
  for (let y = 0; y < leinwand.height; y += seitenHoehe) {
    const h = Math.min(seitenHoehe, leinwand.height - y);
    const stueck = document.createElement('canvas');
    stueck.width = leinwand.width;
    stueck.height = h;
    const g = stueck.getContext('2d');
    if (!g) throw new Error('keine Leinwand');
    g.fillStyle = '#fff';
    g.fillRect(0, 0, stueck.width, h);
    g.drawImage(leinwand, 0, y, leinwand.width, h, 0, 0, leinwand.width, h);
    const blob = await new Promise<Blob | null>((ok) => stueck.toBlob(ok, 'image/jpeg', 0.9));
    if (!blob) throw new Error('kein Bild');
    seiten.push({ jpeg: new Uint8Array(await blob.arrayBuffer()), breite: stueck.width, hoehe: h });
  }
  return new File([pdfAusBildern(seiten)], dateiname, { type: 'application/pdf' });
}

/** Kann dieses Gerät eine PDF-Datei teilen? */
export function kannPdfTeilen(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({
      files: [new File([new Uint8Array([37])], 'probe.pdf', { type: 'application/pdf' })]
    });
  } catch {
    return false;
  }
}
